import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { applicationApi, onboardingApi, profileApi } from '@/api/endpoints'
import { CommandPalette, useCommandPalette } from '@/components/ui/CommandPalette'
import { Kbd } from '@/components/ui/Kbd'
import { Modal } from '@/components/ui/Modal'
import { LivePagePanel } from '@/features/applications/LivePagePanel'
import { UpgradeDialog } from '@/features/billing/UpgradeDialog'
import { useSubscription } from '@/features/billing/useLicense'
import { Sheet } from '@/components/ui/Sheet'
import { ChatPanel } from '@/features/chat/ChatPanel'
import { SparkleIcon } from '@/features/chat/icons'
import { useMailStats } from '@/features/mail/mailMeta'
import { useTheme, type ThemeChoice } from '@/lib/theme'
import type { SubscriptionResponse } from '@/types/api'
import { cn, initials } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import { useChatStore } from '@/store/chat'
import { fromCurrent } from '@/features/applications/backTarget'

/**
 * The app shell.
 *
 * Desktop: a left sidebar (labels from lg, icons only between md and lg) and
 * a slim top bar. Phone: a slim top bar and a bottom tab bar the thumb can
 * reach. Both read from the same NAV list so a badge means the same thing
 * everywhere.
 */
type Badge = 'needsYou' | 'profile' | 'mail'

interface NavItem {
  to: string
  label: string
  /** Shorter word for the phone tab bar. */
  short?: string
  end?: boolean
  icon: (props: { className?: string }) => JSX.Element
  badge?: Badge
  /** Shown in the bottom tab bar on phones. */
  mobile?: boolean
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Home', end: true, icon: HomeIcon, mobile: true },
  { to: '/dashboard/jobs', label: 'Browse jobs', short: 'Jobs', icon: JobsIcon, mobile: true },
  { to: '/dashboard/auto-apply', label: 'Auto apply', short: 'Apply', icon: BoltIcon, mobile: true },
  { to: '/dashboard/review', label: 'Needs review', short: 'Review', icon: InboxIcon, badge: 'needsYou', mobile: true },
  { to: '/dashboard/tracker', label: 'Tracking', icon: TrackerIcon },
  { to: '/dashboard/profile', label: 'Profile', icon: ProfileIcon, badge: 'profile', mobile: true },
  { to: '/dashboard/prep', label: 'Interview prep', short: 'Prep', icon: WarRoomIcon },
  { to: '/dashboard/plans', label: 'Plans', icon: PlanIcon },
  { to: '/dashboard/settings', label: 'Settings', icon: SettingsIcon },
]

/** The plan chip: "Pro · 287 left", or "No plan · Upgrade". Null while loading. */
interface PlanChip {
  label: string
  /** For the narrow pill. */
  short: string
  title: string
  to: string
  /** No plan in force: the chip asks for one. */
  upsell: boolean
  /** In force but nothing left this month. */
  empty: boolean
}

function planChip(data: SubscriptionResponse | undefined): PlanChip | null {
  if (!data) return null
  const sub = data.subscription
  if (data.active && sub) {
    const remaining = data.usage?.remaining
    const left = typeof remaining === 'number' ? `${remaining} left` : 'Active'
    return {
      label: `${sub.planName} · ${left}`,
      short: left,
      title:
        typeof remaining === 'number'
          ? `${sub.planName} plan: ${remaining} applications left this month`
          : `${sub.planName} plan`,
      to: '/dashboard/profile/subscription',
      upsell: false,
      empty: remaining === 0,
    }
  }
  if (sub) {
    return { label: 'Plan ended · Renew', short: 'Renew', title: `${sub.planName} is not active`, to: '/dashboard/plans', upsell: true, empty: false }
  }
  return { label: 'No plan · Upgrade', short: 'Upgrade', title: 'Choose a plan to start applying', to: '/dashboard/plans', upsell: true, empty: false }
}

export function DashboardLayout() {
  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()
  const palette = useCommandPalette()
  const [account, setAccount] = useState(false)
  const location = useLocation()
  const chatOpen = useChatStore((state) => state.open)
  const toggleChat = useChatStore((state) => state.toggle)

  // ⌘J / Ctrl+J: the assistant, from anywhere.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'j') {
        event.preventDefault()
        toggleChat()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleChat])

  useEffect(() => setAccount(false), [location.pathname])

  const { data: summary } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: applicationApi.dashboard,
    staleTime: 30_000,
  })
  // Shares its cache with Settings and Auto Apply, so a toggle there shows here at once.
  const { data: settings } = useQuery({
    queryKey: ['application-settings'],
    queryFn: onboardingApi.getSettings,
    staleTime: 60_000,
  })
  const { data: mailStats } = useMailStats()
  // Profile facts employers keep asking for: counted on the Profile item so they are not missed.
  const { data: profileTodo } = useQuery({
    queryKey: ['profile-recommendations'],
    queryFn: profileApi.recommendations,
    retry: false,
    staleTime: 60_000,
  })

  // The plan and this month's allowance (replaces the old credits count).
  const { data: license } = useSubscription()
  const chip = planChip(license)
  const counts: Record<Badge, number> = {
    needsYou: summary?.stats.needsYou ?? 0,
    profile: profileTodo?.length ?? 0,
    mail: typeof mailStats?.unread === 'number' ? mailStats.unread : 0,
  }
  const badgeFor = (item: NavItem) => {
    if (!item.badge) return null
    const count = counts[item.badge]
    const mail = item.badge === 'needsYou' ? counts.mail : 0
    if (count === 0 && mail === 0) return null
    return {
      count: count || mail,
      tone: count > 0 ? ('warning' as const) : ('brand' as const),
      label:
        item.badge === 'profile'
          ? `${count} profile ${count === 1 ? 'detail' : 'details'} to add`
          : count > 0
            ? `${count} need you`
            : `${mail} unread emails`,
    }
  }

  // Keyboard: "g" then a letter jumps to a section (desktop only, outside inputs).
  useEffect(() => {
    let pending = false
    let timer = 0
    const targets: Record<string, string> = { h: '/dashboard', j: '/dashboard/jobs', a: '/dashboard/auto-apply', t: '/dashboard/tracker', p: '/dashboard/profile', s: '/dashboard/settings' }
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.isContentEditable)) return
      const target2 = pending ? targets[event.key] : undefined
      if (target2) {
        event.preventDefault()
        navigate(target2)
        pending = false
        return
      }
      pending = event.key === 'g'
      window.clearTimeout(timer)
      if (pending) timer = window.setTimeout(() => (pending = false), 1200)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(timer)
    }
  }, [navigate])

  const autoOn = Boolean(settings?.autoApplyEnabled)
  const currentSection = [...NAV].reverse().find((item) => item.end ? location.pathname === item.to : location.pathname.startsWith(item.to))

  return (
    <div className="min-h-dvh bg-canvas md:flex">
      <a href="#main-content" className="sr-only fixed left-4 top-4 z-[100] rounded-field bg-brand px-4 py-3 font-medium text-oncolor focus:not-sr-only">Skip to content</a>
      {/* ---- Sidebar (md+) ---- */}
      <aside className="sticky top-0 hidden h-dvh w-[68px] shrink-0 flex-col border-r border-line bg-surface md:flex lg:w-[240px]">
        <div className="flex h-14 items-center gap-2.5 px-3.5 lg:px-5">
          <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-2.5" aria-label="JobSlide home">
            <LogoMark />
            <span className="hidden text-[17px] font-semibold tracking-tight text-ink lg:inline">JobSlide</span>
          </button>
        </div>

        <p className="label-eyebrow hidden px-6 pb-2 pt-5 text-ink-faint lg:block">Workspace</p>
        <nav aria-label="Main" className="flex flex-1 flex-col gap-0.5 px-2.5 pt-2 lg:px-3">
          {NAV.map((item) => {
            const badge = badgeFor(item)
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={item.label}
                className={({ isActive }) =>
                  cn(
                    'group relative flex h-10 items-center gap-3 rounded-field px-2.5 text-[14px] font-medium transition-colors',
                    isActive ? 'bg-brand-soft text-brand ring-1 ring-brand/10' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span aria-hidden="true" className="absolute -left-2.5 top-2 h-6 w-[3px] rounded-r bg-brand lg:-left-3" />}
                    <item.icon className="h-[18px] w-[18px] shrink-0" />
                    <span className="hidden min-w-0 flex-1 truncate lg:inline">{item.label}</span>
                    {badge && (
                      <span
                        aria-label={badge.label}
                        title={badge.label}
                        className={cn(
                          'num absolute right-1.5 top-1.5 grid h-[16px] min-w-[16px] place-items-center rounded-pill px-1 text-[10px] font-semibold leading-none text-oncolor lg:static',
                          badge.tone === 'warning' ? 'bg-warning' : 'bg-brand',
                        )}
                      >
                        {badge.count > 99 ? '99+' : badge.count}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        <div className="px-2.5 pb-2 lg:px-3">
          <button
            type="button"
            onClick={toggleChat}
            aria-pressed={chatOpen}
            title="Ask the assistant (⌘J)"
            className={cn(
              'flex h-10 w-full items-center gap-3 rounded-field px-2.5 text-[14px] font-medium transition-colors',
              chatOpen ? 'bg-brand-soft text-brand' : 'bg-surface-muted text-ink hover:bg-accent-soft',
            )}
          >
            <SparkleIcon className="h-[18px] w-[18px] shrink-0" />
            <span className="hidden min-w-0 flex-1 truncate text-left lg:inline">Assistant</span>
            <Kbd keys={['mod', 'J']} className="hidden lg:inline-flex" />
          </button>
        </div>

        <div className="space-y-2 border-t border-line p-2.5 lg:p-3">
          <Link
            to="/dashboard/auto-apply"
            className={cn(
              'flex h-10 items-center gap-3 rounded-field px-2.5 text-[13px] font-medium transition-colors',
              autoOn ? 'bg-brand-soft text-brand hover:bg-brand/15' : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
            )}
            title={autoOn ? 'Auto apply is on' : 'Auto apply is off'}
          >
            <span className="relative flex h-2 w-2 shrink-0 items-center justify-center">
              {autoOn && <span className="absolute h-full w-full animate-ping rounded-full bg-brand opacity-60" />}
              <span className={cn('h-2 w-2 rounded-full', autoOn ? 'bg-brand' : 'bg-ink-faint')} />
            </span>
            <span className="hidden lg:inline">Auto apply {autoOn ? 'on' : 'off'}</span>
          </Link>
          <button
            type="button"
            onClick={() => setAccount(true)}
            className="flex w-full items-center gap-3 rounded-field px-1.5 py-1.5 text-left transition-colors hover:bg-surface-muted"
            aria-label="Account"
          >
            <Avatar name={user?.fullName || user?.email} />
            <span className="hidden min-w-0 flex-1 lg:block">
              <span className="block truncate text-[13px] font-medium text-ink">{user?.fullName || user?.email}</span>
              <span className="block truncate text-[11.5px] text-ink-muted">{chip ? chip.label : '\u00a0'}</span>
            </span>
          </button>
        </div>
      </aside>

      {/* ---- Main column ---- */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className="sticky top-0 z-30 border-b border-line bg-surface/90 backdrop-blur-md"
          style={{ paddingTop: 'var(--safe-top)' }}
        >
          <div className="mx-auto flex h-14 w-full max-w-[1280px] 2xl:max-w-[1600px] items-center gap-2 px-4 sm:px-6 lg:px-8">
            <button type="button" onClick={() => navigate('/dashboard')} className="flex items-center gap-2 md:hidden" aria-label="JobSlide home">
              <LogoMark />
              <span className="text-[17px] font-semibold tracking-tight text-ink">JobSlide</span>
            </button>

            <div className="mr-auto hidden min-w-0 items-center gap-2 text-[13px] md:flex">
              <span className="text-ink-muted">Workspace</span>
              <span aria-hidden="true" className="text-ink-faint">/</span>
              <span className="truncate font-medium text-ink">{currentSection?.label ?? 'Application details'}</span>
            </div>
            {/* Search / command: a full field on desktop, an icon on phones. */}
            <button
              type="button"
              onClick={() => palette.setOpen(true)}
              className="ml-auto hidden h-9 w-[280px] items-center gap-2.5 rounded-field border border-line bg-surface-muted px-3 text-[13px] text-ink-muted transition-colors hover:border-line-strong hover:text-ink md:flex"
            >
              <SearchIcon className="h-4 w-4" />
              <span className="flex-1 text-left">Search your workspace</span>
              <Kbd keys={['mod', 'K']} />
            </button>
            <button
              type="button"
              onClick={() => palette.setOpen(true)}
              aria-label="Search"
              className="ml-auto grid h-10 w-10 place-items-center rounded-full text-ink-soft transition-colors hover:bg-surface-muted md:hidden"
            >
              <SearchIcon className="h-5 w-5" />
            </button>

            {/* The assistant: a labelled pill on desktop, an icon beside search on phones. */}
            <button
              type="button"
              onClick={toggleChat}
              aria-label="Ask the assistant"
              aria-pressed={chatOpen}
              className={cn(
                'hidden h-9 items-center gap-2 rounded-pill border px-3 text-[13px] font-medium transition-colors md:inline-flex',
                chatOpen ? 'border-brand/40 bg-brand-soft text-brand' : 'border-line text-ink hover:border-line-strong hover:bg-surface-muted',
              )}
            >
              <SparkleIcon className="h-4 w-4" />
              Ask
              <Kbd keys={['mod', 'J']} />
            </button>
            <button
              type="button"
              onClick={toggleChat}
              aria-label="Ask the assistant"
              aria-pressed={chatOpen}
              className={cn(
                'grid h-10 w-10 place-items-center rounded-full transition-colors md:hidden',
                chatOpen ? 'bg-brand-soft text-brand' : 'text-ink-soft hover:bg-surface-muted',
              )}
            >
              <SparkleIcon className="h-5 w-5" />
            </button>

            {chip && (
              <Link
                to={chip.to}
                title={chip.title}
                className={cn(
                  'num shrink-0 items-center gap-1.5 rounded-pill px-2.5 py-1 text-[12px] font-medium md:hidden',
                  chip.upsell
                    ? 'inline-flex bg-brand text-oncolor'
                    : cn('hidden border border-line sm:inline-flex', chip.empty ? 'text-danger' : 'text-ink-soft'),
                )}
              >
                <SparkIcon />
                {chip.short}
              </Link>
            )}
            <button type="button" onClick={() => setAccount(true)} aria-label="Account" className="md:hidden">
              <Avatar name={user?.fullName || user?.email} />
            </button>
            {chip && (
              <Link
                to={chip.to}
                title={chip.title}
                className={cn(
                  'num hidden shrink-0 items-center gap-1.5 rounded-pill px-3 py-1.5 text-[12.5px] font-medium text-oncolor transition-colors md:inline-flex',
                  chip.upsell ? 'bg-brand hover:bg-brand/90' : chip.empty ? 'bg-danger hover:bg-danger/90' : 'bg-ink hover:bg-ink/90',
                )}
              >
                <SparkIcon />
                {chip.label}
              </Link>
            )}
          </div>
        </header>

        <CaptchaBanner />

        {/* Content and, when open, the assistant side by side; the content shrinks to make room. */}
        <div className="flex min-w-0 flex-1 items-start">
          <main id="main-content" tabIndex={-1} className="page-pad-bottom mx-auto w-full min-w-0 max-w-[1280px] 2xl:max-w-[1600px] flex-1 px-4 pt-5 sm:px-6 sm:pt-7 lg:px-8">
            <Outlet />
          </main>
          <ChatPanel />
        </div>
      </div>

      {/* ---- Bottom tab bar (phones) ---- */}
      <nav
        aria-label="Main"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur-md md:hidden"
        style={{ paddingBottom: 'var(--safe-bottom)' }}
      >
        <ul className="grid h-[var(--tabbar-h)] grid-cols-5">
          {NAV.filter((item) => item.mobile).map((item) => {
            const badge = badgeFor(item)
            return (
              <li key={item.to} className="min-w-0">
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'relative flex h-full flex-col items-center justify-center gap-1 text-[10.5px] font-medium transition-colors',
                      isActive ? 'text-brand' : 'text-ink-muted',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span className={cn('grid h-7 w-12 place-items-center rounded-pill transition-colors', isActive && 'bg-brand-soft')}>
                        <item.icon className="h-[20px] w-[20px]" />
                      </span>
                      <span className="truncate">{item.short ?? item.label}</span>
                      {badge && (
                        <span
                          aria-label={badge.label}
                          className={cn(
                            'num absolute left-1/2 top-1 ml-1.5 grid h-[16px] min-w-[16px] place-items-center rounded-pill px-1 text-[10px] font-semibold leading-none text-oncolor',
                            badge.tone === 'warning' ? 'bg-warning' : 'bg-brand',
                          )}
                        >
                          {badge.count > 99 ? '99+' : badge.count}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            )
          })}
        </ul>
      </nav>

      <CommandPalette open={palette.open} onClose={() => palette.setOpen(false)} />
      <AccountSheet open={account} onClose={() => setAccount(false)} chip={chip} autoOn={autoOn} />
      <UpgradeDialog />
    </div>
  )
}

function AccountSheet({ open, onClose, chip, autoOn }: { open: boolean; onClose: () => void; chip: PlanChip | null; autoOn: boolean }) {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [choice, , setTheme] = useTheme()

  const options: Array<{ value: ThemeChoice; label: string }> = [
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
    { value: 'system', label: 'System' },
  ]

  return (
    <Sheet open={open} onClose={onClose} side="bottom" size="sm" title="Your workspace">
      <div className="flex items-center gap-3 pb-4">
        <Avatar name={user?.fullName || user?.email} size="lg" />
        <div className="min-w-0 flex-1">
          {user?.fullName && <p className="truncate text-[15px] font-semibold text-ink">{user.fullName}</p>}
          <p className="truncate text-[13px] text-ink-muted">{user?.email}</p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-2 pb-4">
        <Link to={chip?.to ?? '/dashboard/plans'} className="panel min-w-0 px-3.5 py-2.5 transition-colors hover:bg-surface-muted">
          <span className="block text-[11.5px] text-ink-muted">Plan</span>
          <span className={cn('block truncate text-[15px] font-semibold leading-[27px]', chip?.upsell ? 'text-brand' : 'text-ink')}>
            {chip ? chip.label : '—'}
          </span>
        </Link>
        <Link to="/dashboard/auto-apply" className="panel px-3.5 py-2.5 transition-colors hover:bg-surface-muted">
          <span className="block text-[11.5px] text-ink-muted">Auto apply</span>
          <span className={cn('block text-[18px] font-semibold', autoOn ? 'text-brand' : 'text-ink')}>{autoOn ? 'On' : 'Off'}</span>
        </Link>
      </dl>

      <div className="pb-4">
        <p className="label-eyebrow mb-2">Theme</p>
        <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-1 rounded-field bg-surface-muted p-1">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={choice === option.value}
              onClick={() => setTheme(option.value)}
              className={cn(
                'h-9 rounded-[8px] text-[13px] font-medium transition-colors',
                choice === option.value ? 'bg-surface text-ink shadow-card' : 'text-ink-muted hover:text-ink',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-line rounded-field border border-line">
        <SheetLink to="/dashboard/profile" icon={<ProfileIcon className="h-4 w-4" />}>Profile</SheetLink>
        <SheetLink to="/dashboard/tracker" icon={<TrackerIcon className="h-4 w-4" />}>Application tracking</SheetLink>
        <SheetLink to="/dashboard/prep" icon={<WarRoomIcon className="h-4 w-4" />}>Interview preparation</SheetLink>
        <SheetLink to="/dashboard/settings" icon={<SettingsIcon className="h-4 w-4" />}>Settings</SheetLink>
        <SheetLink to="/dashboard/plans" icon={<PlanIcon className="h-4 w-4" />}>Plans & billing</SheetLink>
        <SheetLink to="/dashboard/profile/portals" icon={<JobsIcon className="h-4 w-4" />}>Job portals</SheetLink>
      </div>

      <button
        type="button"
        onClick={() => void logout()}
        className="mt-4 h-11 w-full rounded-field text-[14px] font-medium text-danger transition-colors hover:bg-danger/5"
      >
        Log out
      </button>
      <p className="mt-3 hidden text-center text-[12px] text-ink-faint [@media(hover:hover)]:block">
        Tip: press <kbd className="kbd">g</kbd> then <kbd className="kbd">t</kbd> for Tracking, <kbd className="kbd">⌘K</kbd> to search.
      </p>
    </Sheet>
  )
}

function SheetLink({ to, icon, children }: { to: string; icon: ReactNode; children: string }) {
  return (
    <Link to={to} className="flex h-12 items-center gap-3 px-4 text-[14px] font-medium text-ink transition-colors hover:bg-surface-muted">
      <span className="text-ink-muted">{icon}</span>
      <span className="flex-1">{children}</span>
      <ChevronIcon className="h-4 w-4 text-ink-faint" />
    </Link>
  )
}

function Avatar({ name, size = 'md' }: { name: string | null | undefined; size?: 'md' | 'lg' }) {
  return (
    <span
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-accent-soft font-semibold text-ink ring-1 ring-accent-line',
        size === 'lg' ? 'h-12 w-12 text-[15px]' : 'h-9 w-9 text-[12.5px]',
      )}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  )
}

function LogoMark() {
  return (
    <span className="flex h-7 w-7 items-center justify-center rounded-[8px] bg-brand text-oncolor">
      <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
        <path d="M3.25 3.5 6.75 7l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.55" />
        <path d="M7.25 3.5 10.75 7l-3.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  )
}

function SparkIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 1.5 8.3 5.2 12 6.5 8.3 7.8 7 11.5 5.7 7.8 2 6.5l3.7-1.3L7 1.5Z" fill="currentColor" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="m10.5 10.5 3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function ChevronIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="m6 4 4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 9.5 10 3.5l7 6V16a1 1 0 0 1-1 1h-3.5v-4.5h-5V17H4a1 1 0 0 1-1-1V9.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}
function JobsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.5" y="6" width="15" height="10.5" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 6V4.5A1.5 1.5 0 0 1 8.5 3h3A1.5 1.5 0 0 1 13 4.5V6M2.5 10.5h15" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}
function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M11 2.5 4.5 11h5l-.5 6.5L15.5 9h-5l.5-6.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}
function InboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 12h4l1.2 2h3.6L13 12h4M3 12l1.8-6.4A1 1 0 0 1 5.8 5h8.4a1 1 0 0 1 1 .6L17 12m-14 0v3a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}
function TrackerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 11.5 5 4.5h10l2 7m-14 0V15a1.5 1.5 0 0 0 1.5 1.5h11A1.5 1.5 0 0 0 17 15v-3.5m-14 0h4l1 2h4l1-2h4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}
function ProfileIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M3.5 17c.8-3.2 3.4-5 6.5-5s5.7 1.8 6.5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
function PlanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2.5 8.5h15M6 12.5h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}
function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M4.7 15.3l1.4-1.4M13.9 6.1l1.4-1.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

/**
 * A run that hit a CAPTCHA waits a few minutes for its owner to solve it on
 * the application's page. This says so on every page — and, once, out loud:
 * a short tone, the tab title, and a desktop notification when allowed — so
 * the wait is not spent unseen.
 */
function CaptchaBanner() {
  const { applicationId } = useParams()
  const { data } = useQuery({
    queryKey: ['applications-live-waiting'],
    queryFn: applicationApi.liveWaiting,
    refetchInterval: 5000,
    retry: false,
  })
  const all = data ?? []
  const waiting = all.filter((item) => item.applicationId !== applicationId)
  const ids = all.map((item) => item.applicationId).sort().join(',')
  // The pop-up: the parked page itself, opened the moment a run parks, from
  // whichever screen the user is on — solving it here lets the run go on.
  const [popup, setPopup] = useState<string | null>(null)
  const popupItem = all.find((item) => item.applicationId === popup) ?? null

  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  )
  // Only a run that was not waiting a moment ago is announced.
  const announced = useRef<Set<string>>(new Set())
  useEffect(() => {
    const current = ids ? ids.split(',') : []
    const fresh = all.filter((item) => !announced.current.has(item.applicationId))
    announced.current = new Set(current)
    if (fresh.length === 0) return
    // On the application's own page too: a Retry from there parks again, and the pop-up is where it is solved.
    const first = fresh.find((item) => item.applicationId === applicationId) ?? fresh[0]
    if (first) setPopup(first.applicationId)
    chime()
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const first = fresh[0]
      const notice = new Notification('A CAPTCHA is waiting for you', {
        body: `${first?.companyName || first?.jobTitle || 'An application'} — solve it and the application continues by itself.`,
        tag: 'captcha-waiting',
      })
      notice.onclick = () => window.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids])

  useEffect(() => {
    if (all.length === 0) return
    const title = document.title
    document.title = `(${all.length}) CAPTCHA waiting · ${title}`
    return () => {
      document.title = title
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all.length])

  const popupId = popupItem?.applicationId ?? null
  const popupWindow = popupId ? (
    <Modal
      open
      onClose={() => setPopup(null)}
      size="lg"
      title="A site is asking for a CAPTCHA — solve it here"
      description={`${popupItem?.companyName || popupItem?.jobTitle || 'The application'}: click the picture where you would click on the site; the application continues by itself once it clears.`}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Link to={`/dashboard/applications/${popupId}`} state={fromCurrent()} onClick={() => setPopup(null)} className="text-[13px] font-medium text-ink underline underline-offset-2">
            Open the application page →
          </Link>
          <button type="button" onClick={() => setPopup(null)} className="text-[13px] text-ink-muted hover:text-ink">
            Later
          </button>
        </div>
      }
    >
      <LivePagePanel
        source={{
          key: ['application-live', popupId],
          status: () => applicationApi.live(popupId),
          screenshot: () => applicationApi.liveScreenshot(popupId),
          click: (x, y) => applicationApi.liveClick(popupId, x, y),
          type: (text) => applicationApi.liveType(popupId, text),
          scroll: (dy) => applicationApi.liveScroll(popupId, dy),
          reveal: () => applicationApi.liveReveal(popupId),
          done: () => applicationApi.liveDone(popupId),
        }}
        title="The employer's page, live"
        hint="Tick the boxes, pick the pictures, press its button — exactly as you would there."
        alt="The employer's page, live — click to interact"
        idle={<p className="text-[13.5px] text-ink-muted">Cleared — the application is continuing on its own. You can close this.</p>}
        className="panel bg-warning/[0.06] px-3 py-3"
      />
    </Modal>
  ) : null
  if (waiting.length === 0) return popupWindow
  return (
    <>
    {popupWindow}
    <div className="border-b border-warning/30 bg-warning/[0.08]" role="status" aria-live="polite">
      <div className="mx-auto flex w-full max-w-[1280px] 2xl:max-w-[1600px] flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 sm:px-6 lg:px-8">
        <span className="flex items-center gap-2 text-[13px] font-semibold text-ink">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-warning" />
          </span>
          {waiting.length === 1
            ? 'An application is waiting for you to solve a CAPTCHA'
            : `${waiting.length} applications are waiting for you to solve a CAPTCHA`}
        </span>
        {waiting.map((item) => (
          <button
            key={item.applicationId}
            type="button"
            onClick={() => setPopup(item.applicationId)}
            className="text-[13px] font-medium text-warning underline underline-offset-2"
          >
            Solve {item.companyName || item.jobTitle || 'it'} now →
          </button>
        ))}
        {permission === 'default' && (
          <button
            type="button"
            onClick={() => Notification.requestPermission().then(setPermission)}
            className="ml-auto text-[12px] font-medium text-ink-muted underline underline-offset-2"
          >
            Notify me when one is waiting
          </button>
        )}
      </div>
    </div>
    </>
  )
}

/** Two short tones. Silent when the browser has not let this tab play sound yet. */
function chime() {
  try {
    const Context = window.AudioContext
    if (!Context) return
    const context = new Context()
    const now = context.currentTime
    for (const [offset, frequency] of [[0, 880], [0.18, 1175]] as const) {
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      oscillator.frequency.value = frequency
      gain.gain.setValueAtTime(0.0001, now + offset)
      gain.gain.exponentialRampToValueAtTime(0.2, now + offset + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.16)
      oscillator.connect(gain).connect(context.destination)
      oscillator.start(now + offset)
      oscillator.stop(now + offset + 0.18)
    }
    window.setTimeout(() => void context.close(), 800)
  } catch {
    // No sound is fine: the banner and the tab title still say it.
  }
}

/** War Room: a target (prepare for one interview). */
function WarRoomIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="10" cy="10" r="3.6" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="10" cy="10" r="0.9" fill="currentColor" />
    </svg>
  )
}
